import { FetchRequest } from '@rails/request.js';
import AutosubmitController from '@/js/application_controllers/autosubmit_controller';
import { show as showToast } from '@/js/application_controllers/ui/toast_controller';
import { buildSearchParams } from '@/js/util';

export default class extends AutosubmitController {
  static targets = ['instantDetailsInput', 'errorToast'];

  static values = {
    batchSize: 5,
    responseKind: String,
    location: String,
    source: String,
    detailsFormPath: String,
    templatesEditMode: Boolean,
    instantDetails: Boolean,
  };

  #ids;
  #uploads;

  connect() {
    super.connect();
    this.#ids = [];
    this.#uploads = [];
  }

  handleFormSubmit(e) {
    e.preventDefault();
  }

  async submit(event) {
    const files = event.detail.extraPayload;
    const running = new Set();
    let failedCount = 0;

    const run = async () => {
      while (running.size < this.batchSizeValue && files.length) {
        const f = files.shift();
        const request = this._prepareRequest([{ name: 'items[]', value: f.value }])
          .perform()
          .then(async (fetch) => {
            if (fetch.ok) {
              const uploads = await fetch.response.json();
              uploads.forEach((upload) => {
                const normalizedUpload = this.#normalizeUpload(upload);
                this.#uploads.push(normalizedUpload);
                this.#ids.push(normalizedUpload.id);
                this.dispatch('tick', { detail: { name: 'id', value: normalizedUpload.id } });
              });
            } else {
              failedCount += 1;
            }
          })
          .catch(() => {
            failedCount += 1;
          })
          .finally(() => running.delete(request));
        running.add(request);
      }
      if (running.size) {
        await Promise.race(running);
        return run();
      }
    };

    this.dispatch('before-submit', {
      detail: { source: this.sourceValue, location: this.locationValue },
    });
    await run();

    if (failedCount > 0 && this.hasErrorToastTarget) {
      showToast(this.errorToastTarget);
    }

    this.dispatch('switch-sidepanel', {
      detail: { sidepanelId: 'my_library', active: true },
      target: document,
      prefix: false,
    });

    this.dispatch('finished', {
      detail: {
        ids: this.#ids,
        uploads: this.#uploads,
        tabName: this.locationValue,
      },
    });

    const itemIds = this.#uploads
      .filter((upload) => upload.kind === 'item')
      .map((upload) => upload.id);

    if (this.#isInstantDetails && itemIds.length > 0) {
      // TODO: Templates Edit Mode - Refactor with configs
      const query = buildSearchParams({
        ids: itemIds,
        location: this.locationValue,
        templates_edit_mode: this.templatesEditModeValue,
      });

      const detailsFormRequest = new FetchRequest('GET', this.detailsFormPathValue, {
        responseKind: 'turbo-stream',
        query,
      });

      // TODO: provide user feedback once we have notifications/toasts
      detailsFormRequest.perform().catch((e) => console.error(e));
    }
  }

  updateLocation({ target }) {
    this.locationValue = target.value;
  }

  get #isInstantDetails() {
    return this.hasInstantDetailsInputTarget
      ? this.instantDetailsInputTarget.checked
      : this.instantDetailsValue;
  }

  #normalizeUpload(upload) {
    if (typeof upload === 'object' && upload !== null) {
      return {
        id: upload.id,
        kind: upload.kind || 'item',
      };
    }

    return {
      id: upload,
      kind: 'item',
    };
  }
}
